import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MousePointerClick, Users } from 'lucide-react';
import {
  formatEngagementWhen,
  loadLinkClickers,
  type LinkClickerRow,
  type LinkClickersFilter,
} from '../../lib/emailEngagement';
import { ModalBody, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';

function clickerLabel(row: LinkClickerRow): string {
  const email = row.email?.trim();
  if (email) return email;
  const name = row.name?.trim();
  if (name) return name;
  if (row.subscriberId) return row.subscriberId;
  return 'Unknown subscriber';
}

interface LinkClickersModalProps {
  isOpen: boolean;
  onClose: () => void;
  destinationUrl: string;
  filter: LinkClickersFilter;
  timeZone?: string;
}

export default function LinkClickersModal({
  isOpen,
  onClose,
  destinationUrl,
  filter,
  timeZone,
}: LinkClickersModalProps) {
  const titleId = useId();
  const [rows, setRows] = useState<LinkClickerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newsletterId = 'newsletterId' in filter ? filter.newsletterId : undefined;
  const automationStepId =
    'automationStepId' in filter ? filter.automationStepId : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const scopedFilter: LinkClickersFilter = newsletterId
        ? { newsletterId }
        : { automationStepId: automationStepId! };
      setRows(await loadLinkClickers(scopedFilter, destinationUrl));
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : 'Failed to load clickers');
    } finally {
      setLoading(false);
    }
  }, [newsletterId, automationStepId, destinationUrl]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={onClose} closeOnBackdropClick>
      <ModalPanel maxWidthClass="max-w-2xl" titleId={titleId}>
        <ModalHeader
          title="Link clickers"
          description={
            <span className="block truncate" title={destinationUrl}>
              {destinationUrl}
            </span>
          }
          icon={<Users className="w-5 h-5 text-violet-500" />}
          onClose={onClose}
          titleId={titleId}
        />
        <ModalBody>
          {loading ? (
            <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
              Loading clickers…
            </div>
          ) : error ? (
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
              No clickers found for this link.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left py-2 font-medium">Person</th>
                  <th className="text-right py-2 font-medium w-20">Clicks</th>
                  <th className="text-right py-2 font-medium w-36">Last click</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((row) => {
                  const label = clickerLabel(row);
                  return (
                    <tr key={`${row.subscriberId ?? label}-${row.lastClickedAt}`}>
                      <td className="py-2.5 pr-3">
                        {row.subscriberId ? (
                          <Link
                            to={`/subscribers/${row.subscriberId}`}
                            className="text-emerald-600 dark:text-emerald-400 hover:underline"
                            title={label}
                          >
                            {label}
                          </Link>
                        ) : (
                          <span className="text-gray-700 dark:text-gray-200">{label}</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-gray-800 dark:text-gray-200">
                        {row.clickCount}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300 text-xs">
                        {formatEngagementWhen(row.lastClickedAt, timeZone)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </ModalBody>
        {!loading && rows.length > 0 ? (
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <MousePointerClick className="w-3.5 h-3.5" />
            {rows.length} person{rows.length === 1 ? '' : 's'} ·{' '}
            {rows.reduce((sum, row) => sum + row.clickCount, 0)} total click
            {rows.reduce((sum, row) => sum + row.clickCount, 0) === 1 ? '' : 's'}
          </div>
        ) : null}
      </ModalPanel>
    </ModalOverlay>
  );
}
